export const LocationRoutes = {
  getLocations: (siteId?: number) => {
    if (siteId) {
      return `locations?siteId=${siteId}`;
    }
    return `locations`;
  },
  getLocationPricingSchedule: (locationId: string, dayOfMonthIndex: number) =>
    `locations/${locationId}/pricing-schedule/${dayOfMonthIndex}`,
  getLocationEventsCalendar: (locationId: string) => `calendar/events/${locationId}`,
};

export const ScheduleRoutes = {
  runSyncPrices: (siteId: string) => `scheduling/sync-pricing?siteId=${siteId}`,
};

export const AppointmentRoutes = {
  getAvailableSimulatorRentalAppointments: (
    locationId: string,
    partySize: string,
    startDateTime?: string,
    endDateTime?: string,
    email?: string,
  ) => {
    let baseUrl = `appointments/available/simulator?locationId=${locationId}&partySize=${partySize}`;
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    return baseUrl;
  },
  getAvailableSimulatorRentalDates: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
  ) => {
    if (startDateTime && endDateTime) {
      return `appointments/available/dates/simulator?locationId=${locationId}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    return `appointments/available/dates/simulator?locationId=${locationId}`;
  },
  getAvailableStaffAppointments: (
    locationId: string,
    email?: string,
    checkAvailability?: boolean,
  ) => {
    let baseUrl = `appointments/available/staff?locationId=${locationId}`;
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    if (checkAvailability) {
      baseUrl = `${baseUrl}&checkAvailability=${checkAvailability}`;
    }
    return baseUrl;
  },
  getAvailableLessonAppointments: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
    staffIds?: number,
    email?: string,
    includeLastBooked?: boolean,
  ) => {
    let baseUrl = `appointments/available/lesson/?locationId=${locationId}`;
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    if (staffIds) {
      baseUrl = `${baseUrl}&staffIds[]=${staffIds}`;
    }
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    if (includeLastBooked) {
      baseUrl = `${baseUrl}&includeLastBooked=${includeLastBooked}`;
    }
    return baseUrl;
  },
  getStaffAvailability: (
    locationId: string,
    staffId: number,
    startDateTime?: string,
    endDateTime?: string,
    email?: string,
  ) => {
    let baseUrl = `appointments/staff-availability/${staffId}?locationId=${locationId}`;
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    return baseUrl;
  },
  getAvailableClubFittingAppointments: (
    locationId: string,
    startDateTime: string,
    endDateTime: string,
    clubFittingId: number,
    email?: string,
  ) => {
    let baseUrl =
      `appointments/available/club-fitting?locationId=${locationId}&startDateTime=${startDateTime}` +
      `&endDateTime=${endDateTime}&clubFittingId=${clubFittingId}`;
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    return baseUrl;
  },
  getAvailableFirstLessonAppointments: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
    email?: string,
  ) => {
    let baseUrl = `appointments/available/first-time-lesson?locationId=${locationId}`;
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    if (email) {
      const encodedEmail = encodeURIComponent(email);
      baseUrl = `${baseUrl}&email=${encodedEmail}`;
    }
    return baseUrl;
  },
  getAvailableLessonDates: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
    staffId?: number,
    sessionTypeId?: number,
  ) => {
    let baseUrl = `appointments/available/dates/lesson?locationId=${locationId}`;
    if (staffId) {
      baseUrl = `${baseUrl}&staffId=${staffId}`;
    }
    if (sessionTypeId) {
      baseUrl = `${baseUrl}&sessionTypeId=${sessionTypeId}`;
    }
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    return baseUrl;
  },
  getAvailableFirstLessonDates: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
    staffId?: number,
    sessionTypeId?: number,
  ) => {
    let baseUrl = `appointments/available/dates/first-time-lesson?locationId=${locationId}`;
    if (staffId) {
      baseUrl = `${baseUrl}&staffId=${staffId}`;
    }
    if (sessionTypeId) {
      baseUrl = `${baseUrl}&sessionTypeId=${sessionTypeId}`;
    }
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    return baseUrl;
  },
  getAvailableFittingDates: (
    locationId: string,
    startDateTime?: string,
    endDateTime?: string,
    staffId?: number,
  ) => {
    let baseUrl = `appointments/available/dates/club-fitting?locationId=${locationId}`;
    if (staffId) {
      baseUrl = `${baseUrl}&staffId=${staffId}`;
    }
    if (startDateTime && endDateTime) {
      baseUrl = `${baseUrl}&startDateTime=${startDateTime}&endDateTime=${endDateTime}`;
    }
    return baseUrl;
  },
  getClubFittingTypes: (locationId: string) =>
    `appointments/club-fitting-types?locationId=${locationId}`,
  getAppointmentPricing: () => `appointments/pricing`,
  getAppointmentById: (id: string) => `appointments/${id}`,
  getHourlySimulatorPricing: () => `appointments/simulator/hourly-pricing`,
  bookAppointment: (locationId: string) => `appointments/book/${locationId}`,
  verifyPromoCode: (siteId: string, promoCode: string, date: string, sessionTypeIds?: number[]) => {
    const sessionTypeIdString =
      sessionTypeIds && sessionTypeIds.length > 0
        ? sessionTypeIds.map((sessionType) => `&sessionTypeIds[]=${sessionType}`).join('')
        : '';
    return `appointments/promo/verify?siteId=${siteId}&date=${date}&promocode=${promoCode}${sessionTypeIdString}`;
  },
  getDailyPromoCode: (siteId: string, date: string) =>
    `appointments/daily-promos/${siteId}?date=${date}`,
  getClientAppointments: () => `appointments/me`,
  getUpcomingAppointments: () => `appointments/upcoming`,
  cancelAppointment: (locationId: string) => `appointments/cancel/${locationId}`,
  storeBookingData: () => `appointments/booking`,
  retrieveBookingData: (bookingUUID: string) => `appointments/booking/${bookingUUID}`,
  getAllInstructors: (locationId: string) => `appointments/instructors?locationId=${locationId}`,
  sendWaiverLinksToGuests: (locationId: string) => `appointments/book/${locationId}/waiver`,
  checkIn: (locationId: string) => `appointments/check-in/${locationId}`,
  getAppointmentPointsPricing: () => `loyalty/appointment-pricing`,
  getAppointmentPointsToEarn: () => `loyalty/appointment-earning-rules`,
  getLoyaltyTransactionHistory: () => `loyalty/customer-loyalty-history`,
  getMyCards: () => `loyalty/gift-cards`,
  redeemGiftCard: () => `loyalty/redeem`,
  getRewards: (locationId?: string) =>
    locationId ? `loyalty/rewards?locationId=${locationId}` : `loyalty/rewards`,
  postInitialPointsBonus: () => `loyalty/custom-event`,
  getVouchers: (locationId?: string) =>
    locationId ? `loyalty-vouchers/${locationId}` : `loyalty-vouchers`,
  voucherUsage: (voucherId: string) => `loyalty-vouchers/${voucherId}/use`,
  returnGiftCard: (giftCardGAN: string) => `loyalty/gift-cards/return/${giftCardGAN}`,
};

export const FoodOrderRoutes = {
  getFoodItemList: (locationId: string, categoryIds?: string, limit?: number, cursor?: string) => {
    let baseUrl = `store/food-items?locationId=${locationId}`;
    if (categoryIds) {
      baseUrl = `${baseUrl}&categoryIds=${categoryIds}`;
    }
    if (limit) {
      baseUrl = `${baseUrl}&limit=${limit}`;
    }
    if (cursor) {
      baseUrl = `${baseUrl}&cursor=${cursor}`;
    }
    return baseUrl;
  },

  getFoodCategoryList: (locationId: string, startDateTime: string) =>
    `store/categories/${locationId}?startDateTime=${startDateTime}`,

  storePreOrderFoodData: (locationId: string) => `appointments/food-order/${locationId}`,
};

export const AuthRoutes = {
  welcomeScreen: () => `welcome`,
  loginWithEmail: () => `auth/login`,
  loginWithProvider: () => `auth/provider/login`,
  registerPushToken: () => `auth/user/push-token`,
  createAccount: () => `auth/register`,
  refreshToken: () => `auth/refresh`,
  deleteAccount: () => `auth/user`,
  getUserData: (locationId?: string) =>
    locationId ? `auth/user?locationId=${locationId}` : `auth/user`,
  updateUserData: () => `auth/user`,
  verifyUser: (token: string, email: string, redirectToUrl: string) =>
    `auth/verify?token=${token}&email=${email}&redirectTo=${redirectToUrl}`,
  updateBilling: (locationId: string) => `auth/update-billing?locationId=${locationId}`,
  updateUserProvider: () => 'auth/user-provider',
};

export const EventRoutes = {
  sendGtmEvent: () => `event/gtm`,
};

export const CourseRoutes = {
  getCoursesRegions: () => `courses/region`,
  getAvailableCourses: (courseRegionId: string) =>
    `courses/available?courseRegionId=${courseRegionId}`,
  requestTeeTime: () => `courses/request`,
};

export const SupportRoutes = {
  submitTournamentSupportQuestion: () => `support/tournament`,
};

export const RegionalBookingRoutes = {
  getLocationGroup: (locationId: string) => `regional-booking/location-group/${locationId}`,
};
